import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { COPY } from '../constants/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WaitlistSection = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const phoneRegex = /^[\d+\-\s()]{8,20}$/;
    if (!phoneRegex.test(phone.trim())) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        name: name.trim(),
        phone: phone.trim(),
        email,
        role: role || null,
      });

      setIsSuccess(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.detail || 'This email is already on the waitlist!');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      id="waitlist" 
      className="pt-10 pb-20 md:pt-14 md:pb-24 bg-aya-black"
      data-testid="waitlist-section"
    >
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-xl mx-auto"
        >
          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Title */}
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4 text-center">
                  {COPY.waitlist.title}
                </h2>

                {/* Promise */}
                <p className="text-zinc-400 text-base mb-8 text-center">
                  {COPY.waitlist.promise}
                </p>

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  className="mx-auto max-w-[22rem] space-y-5 md:max-w-xl"
                  data-testid="waitlist-form"
                >
                  {/* Name Input */}
                  <div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Full name"
                      required
                      data-testid="waitlist-name-input"
                      className="
                        w-full px-0 py-4
                        bg-transparent border-0 border-b-2 border-white/20
                        text-white text-lg font-heading placeholder-zinc-600
                        focus:outline-none focus:border-aya-yellow
                        transition-colors duration-300
                      "
                    />
                  </div>

                  {/* Phone Input */}
                  <div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Phone number"
                      required
                      data-testid="waitlist-phone-input"
                      className="
                        w-full px-0 py-4
                        bg-transparent border-0 border-b-2 border-white/20
                        text-white text-lg font-heading placeholder-zinc-600
                        focus:outline-none focus:border-aya-yellow
                        transition-colors duration-300
                      "
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      required
                      data-testid="waitlist-email-input"
                      className="
                        w-full px-0 py-4
                        bg-transparent border-0 border-b-2 border-white/20
                        text-white text-lg font-heading placeholder-zinc-600
                        focus:outline-none focus:border-aya-yellow
                        transition-colors duration-300
                      "
                    />
                  </div>

                  {/* Role Select */}
                  <div>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      data-testid="waitlist-role-select"
                      className="
                        w-full px-0 py-4
                        bg-transparent border-0 border-b-2 border-white/20
                        text-white text-lg font-heading
                        focus:outline-none focus:border-aya-yellow
                        transition-colors duration-300
                        cursor-pointer
                        appearance-none
                      "
                      style={{ 
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23525252' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right center'
                      }}
                    >
                      <option value="" className="bg-aya-black">I am a... (optional)</option>
                      <option value="I just love to Dance" className="bg-aya-black">I just love to Dance</option>
                      <option value="I am an Artist" className="bg-aya-black">I am an Artist</option>
                      <option value="I own a Studio" className="bg-aya-black">I own a Studio</option>
                    </select>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-400 text-sm"
                      data-testid="waitlist-error"
                    >
                      {error}
                    </motion.p>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="waitlist-submit-btn"
                    className="
                      hero-outline-btn mt-10 w-auto min-w-[12rem] self-center
                      rounded border border-aya-yellow bg-transparent
                      px-6 py-3.5 text-aya-yellow
                      sm:min-w-[14rem] sm:px-8 sm:py-4
                      font-heading font-bold text-lg
                      disabled:opacity-70 disabled:cursor-not-allowed
                      transition-all duration-300
                      flex items-center justify-center gap-2 mx-auto
                    "
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Joining...
                      </>
                    ) : (
                      COPY.waitlist.button
                    )}
                  </button>

                  {/* Privacy Note */}
                  <p className="text-zinc-600 text-sm text-center mt-4">
                    {COPY.waitlist.privacy}
                  </p>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center py-12"
                data-testid="waitlist-success"
              >
                {/* Success Badge Animation */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-aya-yellow"
                >
                  <Check className="w-10 h-10 text-black" />
                </motion.div>

                {/* Founding Member Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-aya-yellow text-aya-yellow text-sm font-semibold"
                >
                  <Sparkles className="w-4 h-4" />
                  Solid Move!
                </motion.div>

                <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
                  {COPY.waitlist.success}
                </h3>

                <p className="text-zinc-400 text-base">
                  Check your inbox for updates.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default WaitlistSection;
